import { apiGet, apiPost } from '../../../adapters/api-client/api';

export type ReviewSubject = 'TEACHER' | 'COURSE';

/** DEC-S3-P10-10. Only VISIBLE is public; the family sees theirs in any state. */
export type ReviewStatus = 'PENDING' | 'VISIBLE' | 'HIDDEN' | 'REJECTED';

/**
 * A review as the public sees it.
 *
 * There is deliberately no reviewer here. The server does not send one: a
 * parent's name beside their opinion of a children's tutor is not something to
 * publish by default, and the identifier is a digest precisely so it cannot be
 * traced back. What the page can say instead is that the family took the class,
 * which the server guarantees.
 */
export interface PublicReview {
  reviewId: string;
  subject: ReviewSubject;
  subjectId: string;
  rating: number;
  comment?: string;
  createdAt: string;
}

export interface RatingAggregate {
  subject: ReviewSubject;
  subjectId: string;
  totalReviews: number;
  averageRating: number;
}

/**
 * One page of a subject's reviews (PRT-06-FR-05). `aggregate` is over every
 * visible review, not over this page; `nextCursor` is absent on the last page.
 */
export interface SubjectReviews {
  reviews: PublicReview[];
  aggregate: RatingAggregate;
  nextCursor?: string;
}

const pageQuery = (cursor?: string) => (cursor ? `?cursor=${encodeURIComponent(cursor)}` : '');

/** A class the family has taken and not yet rated. */
export interface PendingReview {
  transactionId: string;
  subject: ReviewSubject;
  subjectId: string;
  eligibleSince: string;
  /** When the family can no longer write it (PRT-13-FR-05). */
  eligibleUntil?: string;
}

/** A review this family already wrote (PRT-13-FR-06). */
export interface OwnReview {
  reviewId: string;
  transactionId: string;
  subject: ReviewSubject;
  /** The newest thing the family wrote, which may still be under review. */
  rating: number;
  comment?: string;
  createdAt: string;
  status: ReviewStatus;
  /** The family's newest revision is waiting for a moderator (DEC-S3-P10-11). */
  underReview: boolean;
  /** Sent back with an edit: a stale form is refused rather than overwriting. */
  version: number;
  /** Present while the review window is open. */
  editableUntil?: string;
}

/** One dimension of a submit. With `expectedVersion`, it edits the review. */
export interface ReviewDimension {
  subject: ReviewSubject;
  rating: number;
  comment?: string;
  expectedVersion?: number;
}

export function getTeacherReviews(teacherId: string, cursor?: string): Promise<SubjectReviews> {
  // Public reads (@Public() on the server). Sent with auth, they failed for
  // every signed-out visitor once the course page itself became public.
  return apiGet<SubjectReviews>(`/api/v1/reviews/teachers/${teacherId}${pageQuery(cursor)}`, false);
}

export function getCourseReviews(courseId: string, cursor?: string): Promise<SubjectReviews> {
  return apiGet<SubjectReviews>(`/api/v1/reviews/courses/${courseId}${pageQuery(cursor)}`, false);
}

export function getPendingReviews(): Promise<{ pending: PendingReview[] }> {
  return apiGet('/api/v1/reviews/pending');
}

export function getMyReviews(): Promise<{ reviews: OwnReview[] }> {
  return apiGet('/api/v1/reviews/mine');
}

/**
 * One dimension or both, persisted together or not at all (DEC-S3-P10-08).
 * The teacher and course are resolved server-side from the transaction, so this
 * cannot name a subject the family did not buy.
 */
export function submitReviews(input: {
  transactionId: string;
  dimensions: ReviewDimension[];
}): Promise<{ message: string }> {
  return apiPost('/api/v1/reviews', input);
}
